import { is } from "ramda";

// const DASH: string    = '-';
const DOT = ".";
const ZERO = "0";

export function hasValue(value: BasicValueType): boolean {
  return typeof value !== "undefined" && value !== null;
}

export type BasicValueType = string | number | boolean | undefined | null;
/**
 * Simple comparison function to be used e.g. in .sort, based on two strings and an option to sort descending
 * The parameters a and b must be of the same type or they are .
 * converted to string before comparison.
 *
 * @param a
 * @param b
 * @param ascending
 * @param ignoreCase
 * @param nullEqualsUndefined
 */
export function compareBasicTypes(
  a: BasicValueType,
  b: BasicValueType,
  ascending = true,
  ignoreCase = false,
  nullEqualsUndefined = true,
): number {
  const aHasValue: boolean = hasValue(a);
  const bHasValue: boolean = hasValue(b);

  // Handle most usual case with values first
  if (aHasValue && bHasValue) {
    // string or number of boolean
    if (typeof a !== typeof b) {
      a = a.toString();
      b = b.toString();
    }
    if (is(String, a) && is(String, b)) {
      if (ignoreCase) {
        a = a.toLowerCase();
        b = b.toLowerCase();
      }
    } // both are are string, or number or boolean
    if (a < b) {
      return ascending ? -1 : 1;
    }
    if (a > b) {
      return ascending ? 1 : -1;
    }
    if (typeof a === "number" && typeof b === "number" && (isNaN(a) || isNaN(b))) {
      // Handling of NaN and Inf / -Inf is special
      //   Comparing two NaN results in false for all comparators,
      //   except  !==  which returns true.   So, there is no useful ordering relative to NaN.
      //   For usability we define  NaN to be like undefined. Thus it is lower than all other numbers including -inf
      const aIsNaN = isNaN(a);
      const bIsNaN = isNaN(b);
      return aIsNaN ? (bIsNaN ? 0 : ascending ? -1 : 1) : ascending ? 1 : -1;
    }
    return 0;
  }
  // At least one side is null or undefined.
  if (aHasValue) {
    // a > b, b is null or undefined
    return ascending ? 1 : -1;
  }

  if (bHasValue) {
    // a < b, a is null or undefined
    return ascending ? -1 : 1;
  }

  // A and B are either null or undefined
  if (nullEqualsUndefined) {
    return 0; // null is equal undefined
  }

  // Order of values is:  null < undefined < any-value
  //  reasoning: we want to tell null and undefined apart.
  const aIsNull: boolean = a === null;
  const bIsNull: boolean = b === null;

  if (aIsNull && bIsNull) {
    return 0;
  }

  if (bIsNull) {
    // a is undefined > b is null
    return ascending ? 1 : -1;
  }

  if (aIsNull) {
    // a is null < b is undefined
    return ascending ? -1 : 1;
  }

  return 0; // both are undefined
}

/**
 * Compare two filename with embedded integer numbers and return a number <0, 0, >0 like cmp does.
 *  If twp files start with the same prefix but continue with different sequences of digits
 *   then extract the embedded number and the values are compared
 *  eg: a2 < a10 returns < 0, natural compare by characters would order them as a10 < a2
 *
 * @param a first name
 * @param b second name
 * @param ascending (default true) return <0 if a < b, or revert the order for false
 * @param ignoreCase (default false) compare after converting the name to lower case
 * @param nullEqualsUndefined (default true)
 * @return  number, like cmp does  (<0, 0, >0)
 */

export function compareFileNameWithNumbers(
  a: string,
  b: string,
  ascending = true,
  ignoreCase = false,
  nullEqualsUndefined = true,
): number {
  // test for existence works since type is string and not any
  const aHasValue: boolean = hasValue(a);
  const bHasValue: boolean = hasValue(b);

  // Handle most usual case with values first
  if (aHasValue && bHasValue) {
    if (ignoreCase && is(String, a) && is(String, b)) {
      a = a.toLowerCase();
      b = b.toLowerCase();
    }

    // TODO: find common start, numerical part at the end have to be ignored for the stem
    //   12345                    no stem
    //   file12and14.txt         'file'
    //   file123and4.txt         'file' or 'file123and'
    //   file123and14.txt        'file' or 'file123and'
    //   file123and114.txt       'file' or 'file123and'

    const last = Math.min(a.length, b.length);
    let pos = -1;
    while (last > pos + 1 && a.charAt(pos + 1) === b.charAt(pos + 1)) {
      pos++;
    }

    if (last > 0 && (pos > -1 || (isDigit(a.charAt(0)) && isDigit(a.charAt(0))))) {
      // IF both have text AND there is a stem or first chars are different digits
      // reduce stem if it ends in digits
      while (pos > -1 && isDigit(a.charAt(pos))) {
        pos--;
      }
      let posInA = pos;
      let posInB = pos;
      if (a.length > posInA && isDigit(a.charAt(posInA + 1))) {
        while (posInA < a.length && isDigit(a.charAt(posInA + 1))) {
          posInA++;
        }
      }
      if (b.length > posInB && isDigit(b.charAt(posInB + 1))) {
        while (posInB < b.length && isDigit(b.charAt(posInB + 1))) {
          posInB++;
        }
      }
      if (pos !== posInA && pos !== posInB) {
        // the substring at [pos+1:posInA] contains a number, dito for b
        const aValue: number = Number.parseInt(a.slice(pos + 1, posInA), 10);
        const bValue: number = Number.parseInt(b.slice(pos + 1, posInB), 10);
        if (aValue < bValue) {
          return ascending ? -1 : 1;
        }
        if (aValue > bValue) {
          return ascending ? 1 : -1;
        }
        // Numerical value is identical, now compare the length
        //  '10' < '010' because of length
        if (a.length < b.length) {
          return ascending ? -1 : 1;
        }
        if (a.length > b.length) {
          return ascending ? 1 : -1;
        }
        return 0;
      }
    }

    if (a < b) {
      return ascending ? -1 : 1;
    }

    if (a > b) {
      return ascending ? 1 : -1;
    }
    return 0;
  }
  // At least one side is null or undefined.
  if (aHasValue) {
    // a > b, b is null or undefined
    return ascending ? 1 : -1;
  }

  if (bHasValue) {
    // a < b, a is null or undefined
    return ascending ? -1 : 1;
  }

  // A and B are either null or undefined
  if (nullEqualsUndefined) {
    return 0; // null is equal undefined
  }
  // Order of values is:  null < undefined < any-value
  //  reasoning: we want to tell null and undefined apart.
  const aIsNull: boolean = a === null;
  const bIsNull: boolean = b === null;

  // At least one side is null or undefined.
  if (bIsNull) {
    // a is undefined > b is null
    return ascending ? 1 : -1;
  }

  if (aIsNull) {
    // a is null < b is undefined
    return ascending ? -1 : 1;
  }
  // A and B are either null or undefined
  return 0; // null is equal undefined
}

export function isNumber(v: any) {
  return hasValue(v) && typeof v === "number";
}

export function isDigit(c: string): boolean {
  return 48 <= c.charCodeAt(0) && c.charCodeAt(0) <= 57;
}

export function isNumberStart(c: string): boolean {
  const code: number = c.charCodeAt(0);
  return (
    isDigit(c) ||
    code === 45 || // DASH  '-'
    code === 43 || // PLUS
    code === 46
  ); // DOT
}

export function compareAlphaNumerically(
  cc1: string,
  cc2: string,
  treatAllNumbersAsIntegers = false,
  // if true allow
  ignoreCase = true,
): number {
  // test for existence: it works since type is string and not any
  const aHasValue: boolean = hasValue(cc1);
  const bHasValue: boolean = hasValue(cc2);

  if (!aHasValue || !bHasValue) {
    if (aHasValue) {
      return 1; //  cc1 is greater than undefined cc2
    }
    if (bHasValue) {
      return -1;
    }
    return 0;
  }

  // Interpret full name as double if possible.
  if (isNumberStart(cc1) && isNumberStart(cc2)) {
    const aVal: number = Number.parseFloat(cc1);
    const bVal: number = Number.parseFloat(cc2);
    if (!(Number.isNaN(aVal) || Number.isNaN(bVal))) {
      if (aVal < bVal) {
        return -1;
      }
      if (aVal > bVal) {
        return 1;
      }
      return compareLexically(cc1, cc2);
    }
  }
  let i1 = 0;
  let i2 = 0;
  while (i1 < cc1.length && i2 < cc2.length) {
    let c1: string = cc1[i1];
    let c2: string = cc2[i2];

    if (c1 === DOT && c2 === ZERO) {
      if (i2 + 2 < cc2.length && i1 + 1 < cc1.length) {
        if (cc2[i2 + 1] === DOT && isDigit(cc2[i2 + 2]) && isDigit(cc1[i1 + 1])) {
          i1++;
          i2 += 2;
          c1 = cc1[i1];
          c2 = cc2[i2];
        }
      }
    } else if (c1 === ZERO && c2 === DOT) {
      if (i1 + 2 < cc1.length && i2 + 1 < cc2.length) {
        if (cc1[i1 + 1] === DOT && isDigit(cc1[i1 + 2]) && isDigit(cc2[i2 + 1])) {
          i1 += 2;
          i2++;
          c1 = cc1[i1];
          c2 = cc2[i2];
        }
      }
    }

    if (isDigit(c1) && isDigit(c2)) {
      let len1: number = numLen(cc1, i1);
      let len2: number = numLen(cc2, i2);
      if (treatAllNumbersAsIntegers || i1 === 0 || cc1[i1 - 1] !== DOT) {
        // If it's not a decimal then ignore the leading 0s
        const numLeadingZeros1 = numLeadingZeros(cc1, i1, len1);
        const numLeadingZeros2 = numLeadingZeros(cc2, i2, len2);

        len1 -= numLeadingZeros1;
        len2 -= numLeadingZeros2;

        // If it's not the decimal portion the longest number *is* the largest number
        const lenDiff = len1 - len2;
        if (lenDiff !== 0) {
          return lenDiff;
        }
        i1 += numLeadingZeros1;
        i2 += numLeadingZeros2;
      }
      const len = Math.min(len1, len2);
      for (let i = 0; i < len; i++) {
        const cDiff = cc1.charCodeAt(i1 + i) - cc2.charCodeAt(i2 + i);
        if (cDiff !== 0) {
          return cDiff;
        }
      }
      i1 += len1;
      i2 += len2;
    } else {
      const uc1 = ignoreCase ? c1.toUpperCase() : c1;
      const uc2 = ignoreCase ? c2.toUpperCase() : c2;
      if (uc1 !== uc2) {
        return uc1.charCodeAt(0) - uc2.charCodeAt(0);
      }
      i1++;
      i2++;
    }
  }

  return compareLexically(cc1, cc2);
}

function numLen(cc: string, startIndex: number): number {
  let endIndex = startIndex + 1;
  for (; endIndex < cc.length; endIndex++) {
    if (!isDigit(cc[endIndex])) {
      break;
    }
  }
  return endIndex - startIndex;
}

function numLeadingZeros(cc: string, startIndex: number, len: number): number {
  let zeros = 0;
  for (let i = startIndex; i < startIndex + len; i++) {
    if (cc[i] === ZERO) {
      zeros++;
    } else {
      break;
    }
  }
  return zeros;
}

export function compareLexically(a: string, b: string): number {
  // test for existence works since type is string and not any
  const aHasValue: boolean = hasValue(a);
  const bHasValue: boolean = hasValue(b);

  if (!aHasValue || !bHasValue) {
    if (aHasValue) {
      return 1;
    }
    if (bHasValue) {
      return -1;
    }
    return 0;
  }

  let i = 0;
  const commonLen = Math.min(a.length, b.length);
  while (i < commonLen) {
    const cmp = a.charCodeAt(i) - b.charCodeAt(i);
    if (cmp !== 0) {
      return cmp > 0 ? 1 : -1;
    }
    i++;
  }

  if (a.length === i) {
    if (b.length === i) {
      return 0;
    }
    return -1;
  }
  return 1;
}
